import { createRouter, createWebHistory } from 'vue-router'
import routes from './routes'
import { useAuthStore } from '@/stores/auth'
import { track } from '@/services/analytics'

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

function internalPath(path: string) {
  return path.startsWith('/') && !path.startsWith('//') ? path : '/dashboard/'
}

// Redirects returned from a guard replace the pending navigation, so the
// protected page never mounts and never lands in history.
router.beforeEach((to) => {
  const auth = useAuthStore()
  if (to.meta.auth && !auth.isAuthenticated) {
    return { path: '/login/', query: { redirect: internalPath(to.fullPath) } }
  }
  if (to.meta.guest && auth.isAuthenticated) {
    const target = internalPath(String(to.query.redirect ?? ''))
    // A redirect back onto a guest page (login, register) would bounce forever.
    return router.resolve(target).meta.guest ? '/dashboard/' : target
  }
  if (to.meta.admin && !auth.isAdmin) return '/dashboard/'
  if (to.meta.moderator && !auth.isModerator) return '/dashboard/'
  if (shouldOfferWalletLink(to.path, to.meta.auth, auth.user?.piBrowserLinked)) {
    return { path: '/link-wallet/', query: { redirect: internalPath(to.fullPath) } }
  }
})

// Path only: the Pi Sign-in callback carries its access token in the URL.
// The search text goes along only when it changed, so picking a category or sort
// on Browse doesn't count the same search again.
router.afterEach((to, from, failure) => {
  if (failure) return
  const search = typeof to.query.search === 'string' && to.query.search !== from.query.search ? to.query.search.trim() : ''
  track('Page Viewed', {
    page: to.matched[to.matched.length - 1]?.path ?? to.path,
    path: to.path,
    ...(search && { search }),
  })
})

const WALLET_LINK_OFFERED_KEY = 'ts_wallet_link_offered'

/**
 * Accounts that never signed in through Pi Browser can't receive payouts (see
 * LinkPiWalletView). Signed-in users rarely see the Pi Sign-in callback again,
 * so offer the round trip once per tab session on their first signed-in page.
 */
function shouldOfferWalletLink(path: string, requiresAuth: boolean | undefined, linked: boolean | undefined) {
  if (!requiresAuth || linked !== false || path.startsWith('/admin/')) return false
  try {
    if (sessionStorage.getItem(WALLET_LINK_OFFERED_KEY)) return false
    sessionStorage.setItem(WALLET_LINK_OFFERED_KEY, '1')
  } catch {
    return false
  }
  // Arriving on the link page (e.g. from the Pi Sign-in callback) counts as the offer.
  return path !== '/link-wallet/'
}
